import React, { useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import './LegalPage.css'

const HEADING = /^<b>([\s\S]*)<\/b>$/
const SUBHEADING = /^<u>([\s\S]*)<\/u>$/
const LIST_ITEM = /^<li>([\s\S]*)<\/li>$/

const flatten = (children) => React.Children.toArray(children).join('')

const MailLink = ({ children }) => (
  <a className='legal-page-link' href={`mailto:${flatten(children)}`}>{children}</a>
)

const ExternalLink = ({ children }) => (
  <a className='legal-page-link' href={flatten(children)} target='_blank' rel='noopener noreferrer'>{children}</a>
)

const inlineComponents = {
  b: <strong className='legal-page-strong' />,
  u: <span className='legal-page-highlight' />,
  mail: <MailLink />,
  url: <ExternalLink />
}

const Inline = ({ text }) => (
  <Trans defaults={text} components={inlineComponents} />
)

// Convierte la lista plana de párrafos de i18n en bloques renderizables:
// <b>..</b> solo => encabezado, <u>..</u> solo => subencabezado,
// <li>..</li> consecutivos => una única lista, el resto => párrafo.
const toBlocks = (texts) => {
  const blocks = []

  texts.forEach((text) => {
    const value = String(text).trim()
    const listItem = value.match(LIST_ITEM)

    if (listItem) {
      const last = blocks[blocks.length - 1]

      if (last && last.type === 'list') {
        last.items.push(listItem[1])
        return
      }

      blocks.push({ type: 'list', items: [listItem[1]] })
      return
    }

    const heading = value.match(HEADING)

    if (heading) {
      blocks.push({ type: 'heading', text: heading[1] })
      return
    }

    const subheading = value.match(SUBHEADING)

    if (subheading) {
      blocks.push({ type: 'subheading', text: subheading[1] })
      return
    }

    blocks.push({ type: 'paragraph', text: value })
  })

  return blocks
}

const LegalPage = ({ docKey }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const blocks = useMemo(
    () => toBlocks(t(`legalPages.${docKey}.texts`, { returnObjects: true }) || []),
    [t, docKey]
  )

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [docKey])

  return (
    <section className='legal-page' id={`legal-${docKey}`}>
      <div className='legal-page-hero'>
        <h1 className='legal-page-title'>{t(`legalPages.${docKey}.title`)}</h1>
      </div>

      <article className='legal-page-content'>
        {
          blocks.map((block, index) => {
            if (block.type === 'heading') {
              return <h2 key={index} className='legal-page-heading'>{block.text}</h2>
            }

            if (block.type === 'subheading') {
              return <h3 key={index} className='legal-page-subheading'>{block.text}</h3>
            }

            if (block.type === 'list') {
              return (
                <ul key={index} className='legal-page-list'>
                  {block.items.map((item, itemIndex) => (
                    <li key={itemIndex}><Inline text={item} /></li>
                  ))}
                </ul>
              )
            }

            return <p key={index} className='legal-page-paragraph'><Inline text={block.text} /></p>
          })
        }

        <button type='button' className='legal-page-back' onClick={() => navigate('/')}>
          {t('legalPages.backLabel')}
        </button>
      </article>
    </section>
  )
}

export default LegalPage
